import * as React from "react"
import { cva } from "class-variance-authority"

export const SIDEBAR_COOKIE_NAME = "sidebar:state"
export const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
export const SIDEBAR_WIDTH = "16rem"
export const SIDEBAR_WIDTH_MOBILE = "18rem"
export const SIDEBAR_WIDTH_ICON = "3rem"
export const SIDEBAR_KEYBOARD_SHORTCUT = "b"

export type SidebarContext = {
  state: "expanded" | "collapsed"
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
  isHoverExpanded: boolean
  setHoverExpanded: (expanded: boolean) => void
  hoverToExpandEnabled: boolean
}

export const SidebarContext = React.createContext<SidebarContext | null>(null)

export function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }

  return context
}

/**
 * Optional version of useSidebar that returns null when used outside SidebarProvider.
 * Use this in components that may render both inside and outside SidebarProvider
 * (e.g., AppHeader which is used in both AppLayout and PublicLayout).
 */
export function useSidebarOptional(): SidebarContext | null {
  return React.useContext(SidebarContext)
}

export function useHoverExpand(options: { enabled: boolean; delay?: number }) {
  const { open, setHoverExpanded, hoverToExpandEnabled } = useSidebar()
  const enterTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const leaveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  // Clear timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current)
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    }
  }, [])

  const onMouseEnter = React.useCallback(() => {
    // Clear any pending leave timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }

    // Only expand if hover-to-expand is enabled globally, locally enabled, and sidebar is collapsed
    if (!hoverToExpandEnabled || !options.enabled || open) return

    const delay = options.delay ?? 0
    if (delay > 0) {
      enterTimeoutRef.current = setTimeout(() => {
        setHoverExpanded(true)
      }, delay)
    } else {
      setHoverExpanded(true)
    }
  }, [hoverToExpandEnabled, options.enabled, options.delay, open, setHoverExpanded])

  const onMouseLeave = React.useCallback((event: React.MouseEvent) => {
    // Clear any pending enter timeout
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current)
      enterTimeoutRef.current = null
    }

    // Check if mouse moved to a child element within the sidebar
    // Note: relatedTarget can be Window or other non-Node elements, so we must guard with instanceof
    const relatedTarget = event.relatedTarget
    const currentTarget = event.currentTarget
    if (relatedTarget instanceof Node &&
        currentTarget instanceof Node &&
        currentTarget.contains(relatedTarget)) {
      return // Mouse moved to child element, don't collapse
    }

    // Small delay to prevent flicker when moving between elements
    leaveTimeoutRef.current = setTimeout(() => {
      setHoverExpanded(false)
    }, 100)
  }, [setHoverExpanded])

  // Collapse on window blur
  React.useEffect(() => {
    const handleWindowBlur = () => {
      setHoverExpanded(false)
    }
    window.addEventListener('blur', handleWindowBlur)
    return () => window.removeEventListener('blur', handleWindowBlur)
  }, [setHoverExpanded])

  return { onMouseEnter, onMouseLeave }
}

/**
 * Desktop-only hover expand hook
 * Safely wraps useHoverExpand and returns no-op handlers on mobile
 * to prevent unnecessary hover logic on mobile devices
 */
export function useDesktopHoverExpand(options: { enabled: boolean; delay?: number }) {
  const { isMobile } = useSidebar()

  // Always call the hook unconditionally (React rules of hooks)
  // but disable it on mobile by passing enabled: false
  const hoverHandlers = useHoverExpand({ ...options, enabled: options.enabled && !isMobile })

  // Return no-op handlers on mobile to avoid unnecessary hover logic
  if (isMobile) {
    return {
      onMouseEnter: () => {},
      onMouseLeave: () => {},
    }
  }

  return hoverHandlers
}

export const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:!p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)
